<template lang="pug">
.pp
  .pp-grid
    .pp-title
      product-title.mobile-title(:product="product" :show-h1="true")
    .pp-media(ref="mediaContainer" :class="{ 'pp-media--rackbuilder': rackbuilderWithAccessories, 'pp-media--fit-viewport': fitsMediaToViewport }")
      .pp-media-bg
      .pp-media-content(ref="mediaContent")
        slot(name="media")
          rackbuilder(
            v-if="showsRackbuilder"
            :key="(buyRequest || {}).productId"
            :buyRequest="buyRequest"
            cacheKey="pdp"
            :controls="true"
          )
          Media(v-else :gallery="product.gallery" :product="product")
    .pp-rhpa(ref="rhpaContainer")
      .pp-rhpa-content(ref="rhpaContent")
        product-title.desktop-title(:product="product" :show-h1="true")
        slot(name="rhpa")
          rhpa(v-if="buyRequest" :buy-request="buyRequest" :isCartShipsFreeProduct="isCartShipsFreeProduct" :createRecipe="createProductRecipe")
            template(#rhpaBody)
              RhpaMassStorage(v-if="is3DStorageProduct" :buyRequest='buyRequest')
              RhpaCave(v-else-if="isTheCave" :buyRequest='buyRequest')
              RhpaSlingerX(v-else-if="isSlingerX" :buyRequest='buyRequest')
              RhpaHybridRig(v-else-if="isHybridRig" :buyRequest='buyRequest')
            template(#rhpaTop)
              .cart-ships-free-badge(v-if="isCartShipsFreeProduct")
                StaticImage.img(src="/cms/shipping%20promotions/Free-Cart-Icon_jgqo4q_ju2okk.svg" alt=""
                  :width="100"
                  :height="100"
                  :background="`#ffffff`"
                  style="width: 65px" ssr contain)
                .cart-ships-free-content
                  p.heading-h5 {{ __('Entire Order Ships Free') }}
                  div
                    | {{ __('This Item Qualifies Your Entire Cart To Ship Free. ') }}
                    rogue-link.link(to="/shipping-promotions") {{ __("See Details") }}
              RenderBlock(:block="slots.aboveRhpa")
          loading(v-else)
        slot(name="flexible")
        RenderBlock(:block="slots.flexible" :product="product" :selectedSimple="selectedSimple" ref="underRhpa")
    .pp-gallery(v-if="(slots.gallery || []).length")
      slot(name="gallery")
        RenderBlock(:block="slots.gallery" :product="product" :selectedSimple="selectedSimple")
    .pp-tabs
      slot(name="tabs")
        RenderBlock(:block="slots.tabs" :product="product" v-if="(slots.tabs || []).length")
    .pp-main
      micropackage(:buyRequest="buyRequest" ref="micropackage" v-if="showMicropackage")
      slot(name="mainslot")
        RenderBlock(:block="slots.main" :product="product" :selectedSimple="selectedSimple")
    .pp-full
      .full-content
        RenderBlock(:block="slots.full" :product="product"  :selectedSimple="selectedSimple")
      product-bottom(:product="product")
      slot(name="end")
</template>
<script lang="ts" setup>
import { computed, defineAsyncComponent, onMounted, onUnmounted, ref, watch, toRef } from 'vue'
import { useRoute } from 'vue-router'
import Rhpa from '../../components/rhpa/rhpa.vue'
import { applyRecipeToBuyRequestTree } from './rackbuilder/utils/mms-utils/buy-request-utils'
import ProductTitle from '@/js/components/rhpa/product-title.vue'
import Micropackage from '@/js/pages/product/components/micropackage.vue'
import ProductBottom from '@/js/pages/product/product-bottom.vue'
import { getSimpleForBuyRequest } from '@/js/helpers/buy-request'
import { useRogue } from '@/js/services/Rogue'
import { ALL_RACKBUILDER } from '@/js/stores/productConstants'
import { isEnabledCustomProduct } from '@/js/customProducts/registry'
import { setupScrollListener } from '@/js/pages/product/rackbuilder/components/ui/scroll/scrollManager'
import type { BuyRequest, Product } from '@/js/services/RogueTypes'
import { useMassStorage } from '@/js/composables/useMassStorage'

const RhpaMassStorage = defineAsyncComponent(() => import('@/js/components/rhpa/rhpa-mass-storage.vue'))
const RhpaCave = defineAsyncComponent(() => import('@/js/components/rhpa/rhpa-cave.vue'))
const RhpaSlingerX = defineAsyncComponent(() => import('@/js/components/rhpa/rhpa-slinger-x.vue'))
const RhpaHybridRig = defineAsyncComponent(() => import('@/js/components/rhpa/rhpa-hybrid-rig.vue'))

const route = useRoute()
const rogue = useRogue()
const props = defineProps<{
  product?: Product,
  buyRequest?: BuyRequest
}>()

const buyRequestRef = toRef(props, 'buyRequest')
const { is3DStorageProduct } = useMassStorage(buyRequestRef)

const slots = computed(() => ((props.product?.roguebuilder || {}).slots || {}))
const rbProps = computed(() => ((props.product?.roguebuilder || {}).props || {}))

const showsRackbuilder = computed(() => Boolean(
  props.buyRequest && !props.buyRequest.loading
  && (ALL_RACKBUILDER.includes(props.product?.sku.toUpperCase() ?? '') || rbProps.value.rackbuilder?.type)
))

const rackbuilderWithAccessories = computed(() => showsRackbuilder.value && props.buyRequest?.type === 'group')

// The trap bar is short and wide, so its 3D view stops at the bottom of the viewport instead of running below the fold like other rackbuilders.
const fitsMediaToViewport = computed(() => rbProps.value.rackbuilder?.type === 'tb3')

const showMicropackage = computed(() => props.product?.micropackageIds?.length && props.buyRequest && !props.buyRequest.loading)

const selectedSimple = computed(() => (props.buyRequest ? getSimpleForBuyRequest(props.buyRequest, rogue) : null))

const isCartShipsFreeProduct = computed(() => (selectedSimple.value?.attributes?.shipperhq_shipping_group || []).includes('FREE_CART'))

const isTheCave = computed(() => isEnabledCustomProduct(rogue, 'cave', props.product))
const isSlingerX = computed(() => isEnabledCustomProduct(rogue, 'slinger-x', props.product))
const isHybridRig = computed(() => isEnabledCustomProduct(rogue, 'hybrid-rig', props.product))

async function createProductRecipe() {
  if (!is3DStorageProduct.value && !isTheCave.value && !isSlingerX.value && !isHybridRig.value) return false

  let recipeData

  try {
    recipeData = await rogue.fetch('/svc_zeus/product/configs', 'post', {
      buyRequest: props.buyRequest,
    })

    console.log('Recipe created:', recipeData)

    if (props.buyRequest) {
      // Add the recipe to each item in the BR
      applyRecipeToBuyRequestTree(props.buyRequest, recipeData)
    }
  } catch (error) {
    rogue.log.error('Error creating Mass Storage recipe', error as Error)
  }

  return true
}

// Refs for DOM elements
const rhpaContent = ref<HTMLElement | null>(null)
const mediaContent = ref<HTMLElement | null>(null)
const mediaContainer = ref<HTMLElement | null>(null)

// Written straight to the element, not through Vue, so the engine's own resize handler reads the new height in the same event.
function updateMediaTop() {
  mediaContainer.value?.style.setProperty('--pp-media-top', `${mediaContainer.value.getBoundingClientRect().top + window.scrollY}px`)
}

onMounted(() => {
  // Registered before the scroll manager and the engine so both measure the capped height.
  if (fitsMediaToViewport.value) {
    updateMediaTop()
    window.addEventListener('resize', updateMediaTop)
  }

  const { unmount, resetRhpaScroll } = setupScrollListener({ mediaContainer, mediaContent, rhpaContent, fitMediaToViewport: fitsMediaToViewport.value })

  // When a recipe thumbnail is selected, the rid query param changes — reset the RHPA scroll position.
  if (isTheCave.value || is3DStorageProduct.value || isHybridRig.value) {
    watch(() => route.query.rid, (newRid, oldRid) => {
      if (newRid && newRid !== oldRid) {
        resetRhpaScroll()
      }
    })
  }

  // Cleanup
  onUnmounted(() => {
    unmount()
    window.removeEventListener('resize', updateMediaTop)
  })
})

</script>

<style lang="scss" scoped>
.desktop-title {
  display: none;

}
@include all-desktop(){
  .desktop-title {
    display: block;
  }
  .mobile-title {
    display: none;
  }
}

.pp {
  padding: 0 $space-s;
  max-width: 1920px;
  margin: 0 auto;
  min-width: 0;

  @include desktop(){
    padding: 54px $space-l 0 $space-xxl; // 16px on the right comes from the RHPA padding. 48px on both sides
  }

  @include large-xl(){
    padding: 54px 56px 0 $space-xxxxl; //RHPA is 16px: 56px + 16 = 72
  }
}

.pp-media-bg {
  background: #f8f8f8;
  margin-left: -1000px;
  width: calc(100vw + 1000px);
  position: absolute;
  z-index: -1;
  top: 0;
  bottom: 0;

  @include all-desktop(){
    top: -54px;
  }
}

.pp-grid {
  display: grid;
  gap: 0 $space-s;
  grid-template-columns: 1fr;
  grid-template-areas: "title" "hero" "media" "rhpa" "gallery" "tabs" "micro" "main" "full";
  grid-auto-rows: min-content;

  @include all-desktop(){
    grid-template-areas: "media rhpa" "gallery rhpa" "tabs rhpa" "main rhpa" "fill rhpa" "full full";
    grid-template-rows: min-content min-content min-content min-content min-content min-content 1fr min-content;
  }

  @include desktop(){
    grid-template-columns: minmax(300px, 1fr) 407px; //right column is 375px + 16 * 2 padding
    //padding: 0 $space-l 0 $space-xxl; // 16px on the right comes from the RHPA padding. 48px on both sides
    gap: 0 $space-s;
    .pp-media {
      margin-left: -$space-xl;
      width: calc(100vw - 476px);
    }
  }

  @include large-xl(){
    grid-template-columns: minmax(300px, 1fr) 452px; //right column is 420px + 16px * 2 padding
    //padding: 0 56px 0 $space-xxxxl; //RHPA is 16px: 56px + 16 = 72
    .pp-media {
      margin-left: -64px;
      width: calc(100vw - 562px);
    }
  }

  @include large(){
    gap: 0 $space-l;
  }

  @include xl(){
    gap: 0 56px;
    .pp-media {
      margin-left: calc((-1 * (100vw - 1920px + 176px) / 2) + 24px);
      width: calc(100vw - 570px - ((100vw - 1920px) / 2))
    }
  }
}

.pp-full {
  grid-area: full;
  margin: $space-l 0;
  //background: lightgrey;
}

.pp-title, .pp-media, .pp-rhpa, .pp-gallery, .pp-tabs, .pp-main, .pp-full {
  width: 100%;
  // Must set min-width or else the column has min-width: content (and will expand)
  min-width: 0;
}

.pp-title {
  grid-area: title;
  background: white;
  //background: lightblue;
}

.pp-media {
   aspect-ratio: 4 / 3;

   @media (max-width: 500px) {
    aspect-ratio: 16 / 14;
   }

  @include tablet() {
    aspect-ratio: 16 / 9;
  }

  @include all-desktop(){
    &.pp-media--rackbuilder{
    aspect-ratio: 4 / 5;
   }

    &.pp-media--fit-viewport {
      aspect-ratio: auto;
      height: min(calc(100vh - var(--pp-media-top, #{$space-s}) - #{$space-s}), 1200px);
    }
  }

  &:has(.pp-media-bg){
    overflow-x: clip
  }
}

.pp-media {
  grid-area: media;
  //background: lightcoral;
  position: relative;

  .pp-media-content {
    position: sticky;
  }

  @include all-desktop(){
    min-height: fit-content;
    transition: height 0.3s ease;

    .pp-media-content{
      width: 100%;
      height: 100%;
      max-height: min(calc(100vh - 32px), 1200px);
      transition: top 0.3s ease;
    }
  }

  @include not-desktop(){
    position: sticky;
    transition: top 0.3s ease;
    width: auto;
    margin-left: -$space-s;
    margin-right: -$space-s;
    z-index: 10;
  }
}

@include all-desktop(){
  .pp-title {
    display: none;
  }
}

.pp-rhpa {
  grid-area: rhpa;
  //background: lightgoldenrodyellow;
  //height: min-content;
  background: white;
}

.pp-rhpa-content {
  padding-top: $space-s;
  background: white;
  z-index: 1;

  @include not-mobile(){
    padding: $space-s;
  }

  @include all-desktop() {
   position: sticky;
  }
}

.pp-gallery {
  grid-area: gallery;
  margin-top: $space-m;
}

.pp-tabs {
  grid-area: tabs;
  //background: lightgray;
  margin-top: $space-m;
}

.pp-main {
  grid-area: main;
  //background: lightskyblue;
}

.cart-ships-free-badge {
  display: flex;
  margin-top: $space-xs;
}

.cart-ships-free-content {
  padding: $space-xs;
  .heading-h5 {
    margin-bottom: $space-xxxs;
  }
  div {
    font-size: $size-s;
  }
}
</style>
